import { $isMarkNode, $wrapSelectionInMarkNode } from "@lexical/mark";
import type { MarkNode as LexicalMarkNode } from "@lexical/mark";
import {
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isTextNode,
  $setSelection,
  CLEAR_HISTORY_COMMAND,
  type LexicalEditor,
  type LexicalNode,
  type TextNode,
} from "lexical";
import type { MarkItem, ProofreadingIssueStatus } from "@/types/proofreading";
import {
  $createProofreadingMarkNode,
  $isProofreadingMarkNode,
} from "@/lib/lexical/ProofreadingMarkNode";

const MARK_STATUS_CLASSES = [
  "lexical-editor__mark--pending",
  "lexical-editor__mark--rejected",
  "lexical-editor__mark--applied",
] as const;

const MARK_STATUS_CLASS_BY_STATUS: Record<ProofreadingIssueStatus, string> = {
  pending: "lexical-editor__mark--pending",
  rejected: "lexical-editor__mark--rejected",
  applied: "lexical-editor__mark--applied",
};

const MARK_HOVERED_CLASS = "lexical-editor__mark--hovered";

function collectTextNodesInOrder(node: LexicalNode, result: TextNode[]): void {
  if ($isTextNode(node)) {
    result.push(node);
    return;
  }
  if ($isElementNode(node)) {
    node.getChildren().forEach((child) => collectTextNodesInOrder(child, result));
  }
}

/** Map a UTF-16 offset in `$getRoot().getTextContent()` to a text-point selection. */
function $getTextPointAtOffset(absoluteOffset: number): {
  key: string;
  offset: number;
} | null {
  const textNodes: TextNode[] = [];
  $getRoot().getChildren().forEach((child) => collectTextNodesInOrder(child, textNodes));

  if (textNodes.length === 0) {
    return null;
  }

  let remaining = absoluteOffset;
  for (let i = 0; i < textNodes.length; i += 1) {
    const textNode = textNodes[i];
    const size = textNode.getTextContentSize();
    if (remaining < size || (remaining === size && i === textNodes.length - 1)) {
      return { key: textNode.getKey(), offset: remaining };
    }
    remaining -= size;
  }

  const last = textNodes[textNodes.length - 1];
  return { key: last.getKey(), offset: last.getTextContentSize() };
}

function $wrapMarkItem(item: MarkItem, contentLength: number): void {
  const start = Math.max(0, item.start);
  const end = Math.min(contentLength, item.end);
  if (start >= end) return;

  const anchor = $getTextPointAtOffset(start);
  const focus = $getTextPointAtOffset(end);
  if (!anchor || !focus) return;

  const selection = $createRangeSelection();
  selection.anchor.set(anchor.key, anchor.offset, "text");
  selection.focus.set(focus.key, focus.offset, "text");

  $wrapSelectionInMarkNode(
    selection,
    false,
    item.id,
    (ids) => $createProofreadingMarkNode(ids),
  );
}

export function updateMarkItems(
  targetEditor: LexicalEditor,
  content: string,
  markItems: MarkItem[],
): void {
  // 再構築前の Undo で戻れないよう、履歴を空にしてから起点を作り直す
  targetEditor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);

  targetEditor.update(
    () => {
      const root = $getRoot();
      root.clear();

      const paragraphNode = $createParagraphNode();
      paragraphNode.append($createTextNode(content));
      root.append(paragraphNode);

      // Longer ranges first so outer wraps tend to enclose later inner ones.
      const sortedItems = [...markItems].sort(
        (a, b) => (b.end - b.start) - (a.end - a.start) || a.start - b.start,
      );

      sortedItems.forEach((item) => {
        $wrapMarkItem(item, content.length);
      });
    },
    { discrete: true },
  );
}

export function paintMarkDomAppearance(
  targetEditor: LexicalEditor,
  statusById: Map<string, ProofreadingIssueStatus>,
  hoveredMarkId: string | null,
): void {
  targetEditor.getEditorState().read(() => {
    function walk(node: LexicalNode): void {
      if ($isProofreadingMarkNode(node)) {
        const element = targetEditor.getElementByKey(node.getKey());
        if (element instanceof HTMLElement) {
          const [markId] = node.getIDs();
          const status = (markId && statusById.get(markId)) || "pending";
          const statusClass = MARK_STATUS_CLASS_BY_STATUS[status];

          MARK_STATUS_CLASSES.forEach((className) => {
            element.classList.toggle(className, className === statusClass);
          });
          element.classList.toggle(
            MARK_HOVERED_CLASS,
            hoveredMarkId != null && node.getIDs().includes(hoveredMarkId),
          );
        }
      }
      if ($isElementNode(node)) {
        node.getChildren().forEach(walk);
      }
    }

    $getRoot().getChildren().forEach(walk);
  });
}

export function buildMarkStatusById(markItems: MarkItem[]): Map<string, ProofreadingIssueStatus> {
  return new Map(markItems.map((item) => [item.id, item.status]));
}

function getMarkTextRange(markNodes: LexicalMarkNode[]): {
  startNode: TextNode;
  endNode: TextNode;
} | null {
  if (markNodes.length === 0) return null;

  const startTexts: TextNode[] = [];
  const endTexts: TextNode[] = [];
  collectTextNodesInOrder(markNodes[0], startTexts);
  collectTextNodesInOrder(markNodes[markNodes.length - 1], endTexts);

  if (startTexts.length === 0 || endTexts.length === 0) return null;

  return {
    startNode: startTexts[0],
    endNode: endTexts[endTexts.length - 1],
  };
}

export function collectMarkNodesById(
  node: LexicalNode,
  markId: string,
  result: LexicalMarkNode[],
): void {
  if ($isMarkNode(node)) {
    if (node.getIDs().includes(markId)) {
      result.push(node);
    }
  }
  if ($isElementNode(node)) {
    node.getChildren().forEach((child) => collectMarkNodesById(child, markId, result));
  }
}

export function getEditorPlainText(targetEditor: LexicalEditor): string {
  let text = "";
  targetEditor.getEditorState().read(() => {
    text = $getRoot().getTextContent();
  });
  return text;
}

/**
 * 各 Mark ID の、エディタ全文プレーンテキスト上での開始 UTF-16 オフセットを返す。
 * 同一 ID が複数ノードに分かれている場合は最初の出現を採用する。
 */
export function getEditorMarkStartOffsets(
  targetEditor: LexicalEditor,
): Map<string, number> {
  const offsets = new Map<string, number>();

  targetEditor.getEditorState().read(() => {
    const textNodes: TextNode[] = [];
    $getRoot().getChildren().forEach((child) => collectTextNodesInOrder(child, textNodes));

    const offsetByTextKey = new Map<string, number>();
    let running = 0;
    textNodes.forEach((textNode) => {
      offsetByTextKey.set(textNode.getKey(), running);
      running += textNode.getTextContentSize();
    });

    function walk(node: LexicalNode): void {
      if ($isMarkNode(node)) {
        const markNodes: LexicalMarkNode[] = [node];
        const textRange = getMarkTextRange(markNodes);
        if (textRange) {
          const startOffset = offsetByTextKey.get(textRange.startNode.getKey()) ?? 0;
          node.getIDs().forEach((id) => {
            if (!offsets.has(id)) {
              offsets.set(id, startOffset);
            }
          });
        }
      }
      if ($isElementNode(node)) {
        node.getChildren().forEach(walk);
      }
    }

    $getRoot().getChildren().forEach(walk);
  });

  return offsets;
}

/**
 * Mark 内テキストを置換する。
 * - 同一 ID の複数断片がある場合でも、断片間のテキストは選択・削除しない
 * - selection.insertText は使わず、Mark ノードの子を直接差し替える
 *   （MarkNode の canInsertTextBefore/After: false による破壊的パスを避ける）
 */
export function replaceTextAtMark(
  targetEditor: LexicalEditor,
  markId: string,
  replacementText: string,
): boolean {
  let replaced = false;

  targetEditor.update(
    () => {
      const markNodes: LexicalMarkNode[] = [];
      $getRoot().getChildren().forEach((child) => collectMarkNodesById(child, markId, markNodes));
      if (markNodes.length === 0) return;

      // 後方断片から削除（いずれも当該 Mark のテキスト）。断片間の非 Mark テキストは触らない。
      for (let i = markNodes.length - 1; i >= 1; i -= 1) {
        markNodes[i].remove();
      }

      const primary = markNodes[0];
      if (replacementText === "") {
        primary.remove();
      } else {
        // MarkNode は canBeEmpty: false のため、子を先に全部消すと Mark 自体が消える。
        // 新しい Text を先に append してから旧子を除去する。
        const previousChildren = [...primary.getChildren()];
        primary.append($createTextNode(replacementText));
        previousChildren.forEach((child) => child.remove());
      }

      replaced = true;
    },
    { discrete: true },
  );

  return replaced;
}

export function replaceTextAtUtf16Range(
  targetEditor: LexicalEditor,
  startUtf16: number,
  oldUtf16Len: number,
  newText: string,
  expectedOldText?: string,
): boolean {
  const plainBefore = getEditorPlainText(targetEditor);
  const oldSlice = plainBefore.slice(startUtf16, startUtf16 + oldUtf16Len);
  if (oldUtf16Len > 0 && oldSlice.length !== oldUtf16Len) {
    return false;
  }
  if (expectedOldText != null && oldSlice !== expectedOldText) {
    return false;
  }

  const plainAfter =
    plainBefore.slice(0, startUtf16) + newText + plainBefore.slice(startUtf16 + oldUtf16Len);
  let replaced = false;

  targetEditor.update(
    () => {
      const root = $getRoot();
      root.clear();

      const paragraphNode = $createParagraphNode();
      paragraphNode.append($createTextNode(plainAfter));
      root.append(paragraphNode);
      replaced = true;
    },
    { discrete: true },
  );

  return replaced;
}

export function applySuggestionAtMark(
  targetEditor: LexicalEditor,
  markId: string,
  replacementText: string,
): boolean {
  return replaceTextAtMark(targetEditor, markId, replacementText);
}

export function revertSuggestionAtMark(
  targetEditor: LexicalEditor,
  markId: string,
  originalText: string,
): boolean {
  return replaceTextAtMark(targetEditor, markId, originalText);
}

export function moveCursorToUtf16Offset(
  targetEditor: LexicalEditor,
  absoluteOffset: number,
): string | null {
  let textNodeKey: string | null = null;

  targetEditor.update(
    () => {
      const anchor = $getTextPointAtOffset(absoluteOffset);
      if (!anchor) return;

      const selection = $createRangeSelection();
      selection.anchor.set(anchor.key, anchor.offset, "text");
      selection.focus.set(anchor.key, anchor.offset, "text");
      $setSelection(selection);
      textNodeKey = anchor.key;
    },
    { discrete: true },
  );

  return textNodeKey;
}

export function moveCursorToMarkStart(targetEditor: LexicalEditor, markId: string): boolean {
  let moved = false;

  targetEditor.update(
    () => {
      const markNodes: LexicalMarkNode[] = [];
      $getRoot().getChildren().forEach((child) => collectMarkNodesById(child, markId, markNodes));
      if (markNodes.length === 0) return;

      // 先頭断片のみを選択（断片間への選択拡大を避ける）
      const textRange = getMarkTextRange([markNodes[0]]);
      if (!textRange) return;

      const { startNode, endNode } = textRange;
      const selection = $createRangeSelection();
      selection.anchor.set(startNode.getKey(), 0, "text");
      selection.focus.set(endNode.getKey(), endNode.getTextContentSize(), "text");
      $setSelection(selection);
      moved = true;
    },
    { discrete: true },
  );

  return moved;
}
